import React, { useState, useRef, useCallback } from 'react';
import { LuMic, LuCircleStop, LuLoaderCircle, LuTrash2 } from 'react-icons/lu';
import { analyzeAudioLevel, isLikelyNoiseResult, encodeWAV } from '../utils/audioAnalysis';

const TEST_SCENARIOS = [
  { id: 'speech', label: 'Normal Speech', desc: 'Talk normally into the mic', expectPass: true },
  { id: 'breathing', label: 'Breathing While Talking', desc: 'Breathe audibly while speaking', expectPass: true },
  { id: 'quiet', label: 'Quiet Speech', desc: 'Speak softly, do not yell', expectPass: true },
  { id: 'whisper', label: 'Whisper', desc: 'Whisper into the mic', expectPass: true },
  { id: 'table_bang', label: 'Table Banging', desc: 'Bang on the table 3-4 times', expectPass: false },
  { id: 'clap', label: 'Clapping', desc: 'Clap hands 3-4 times', expectPass: false },
  { id: 'silence', label: 'Silence', desc: 'Do not make any sound', expectPass: false },
  { id: 'static', label: 'Fan / Static Noise', desc: 'Record near a fan or AC unit', expectPass: false },
];

function ResultBadge({ passed, expected }) {
  const correct = passed === expected;
  return (
    <span className={`xi-badge ${correct ? 'xi-badge--ok' : 'xi-badge--bad'}`}>
      {correct ? 'CORRECT' : 'WRONG'}
    </span>
  );
}

export default function AudioAnalysisTest() {
  const [isRecording, setIsRecording] = useState(false);
  const [selectedScenario, setSelectedScenario] = useState('speech');
  const [results, setResults] = useState([]);
  const [liveMetrics, setLiveMetrics] = useState(null);
  const [predictResult, setPredictResult] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const audioChunks = useRef([]);
  const audioContextRef = useRef(null);
  const sourceRef = useRef(null);
  const analyserRef = useRef(null);
  const dataArrayRef = useRef(null);
  const animationIdRef = useRef(null);
  const waveformCanvas = useRef(null);
  const processorRef = useRef(null);
  const autoStopRef = useRef(null);
  const intervalRef = useRef(null);
  const [timer, setTimer] = useState(8);

  const startRecording = useCallback(async () => {
    try {
      setPredictResult(null);
      setLiveMetrics(null);
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1,
          sampleRate: 48000
        }
      });

      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      sourceRef.current = audioContextRef.current.createMediaStreamSource(stream);
      analyserRef.current.fftSize = 2048;
      const bufferLength = analyserRef.current.frequencyBinCount;
      dataArrayRef.current = new Uint8Array(bufferLength);

      function drawWave() {
        const canvas = waveformCanvas.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const width = canvas.width;
        const height = canvas.height;
        ctx.clearRect(0, 0, width, height);
        analyserRef.current.getByteTimeDomainData(dataArrayRef.current);
        ctx.beginPath();
        ctx.lineWidth = 2;
        ctx.strokeStyle = '#2bffbb';
        const sliceWidth = width / bufferLength;
        let x = 0;
        for (let i = 0; i < bufferLength; i++) {
          const v = dataArrayRef.current[i] / 128.0;
          const y = (v * height) / 2;
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
          x += sliceWidth;
        }
        ctx.stroke();
        animationIdRef.current = requestAnimationFrame(drawWave);
      }
      drawWave();

      const processor = audioContextRef.current.createScriptProcessor(4096, 1, 1);
      audioChunks.current = [];
      processor.onaudioprocess = (e) => {
        const channelData = e.inputBuffer.getChannelData(0);
        audioChunks.current.push(new Float32Array(channelData));

        // Live metrics
        let sum = 0;
        let maxAbs = 0;
        for (let i = 0; i < channelData.length; i++) {
          const abs = Math.abs(channelData[i]);
          sum += abs;
          if (abs > maxAbs) maxAbs = abs;
        }
        setLiveMetrics({
          avgAmplitude: (sum / channelData.length).toFixed(5),
          peakAmplitude: maxAbs.toFixed(5),
          chunkCount: audioChunks.current.length,
        });
      };

      sourceRef.current.connect(analyserRef.current);
      analyserRef.current.connect(processor);
      processor.connect(audioContextRef.current.destination);
      processorRef.current = processor;

      setIsRecording(true);
      setTimer(8);
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = setInterval(() => setTimer(t => Math.max(t - 1, 0)), 1000);
      if (autoStopRef.current) clearTimeout(autoStopRef.current);
      autoStopRef.current = setTimeout(() => stopRecording(), 8000);
    } catch (e) {
      alert('Microphone access denied: ' + e.message);
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    if (animationIdRef.current) {
      cancelAnimationFrame(animationIdRef.current);
      animationIdRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (autoStopRef.current) {
      clearTimeout(autoStopRef.current);
      autoStopRef.current = null;
    }
    setIsRecording(false);
    analyzeRecording();
  }, []);

  const analyzeRecording = useCallback(async () => {
    setIsAnalyzing(true);
    try {
      const pcmArrays = audioChunks.current;
      const totalLength = pcmArrays.reduce((sum, arr) => sum + arr.length, 0);
      const merged = new Float32Array(totalLength);
      let offset = 0;
      for (let arr of pcmArrays) {
        merged.set(arr, offset);
        offset += arr.length;
      }

      const wavBlob = encodeWAV(merged, 44100);

      // Client-side analysis
      const analysis = await Promise.race([
        analyzeAudioLevel(wavBlob),
        new Promise(resolve => setTimeout(() => resolve({ isSilent: false, reason: 'timeout' }), 5000))
      ]);

      const scenario = TEST_SCENARIOS.find(s => s.id === selectedScenario);
      const clientPass = !analysis.isSilent;
      const correct = clientPass === scenario.expectPass;

      // Try predict API
      let apiResult = null;
      let apiPass = null;
      let apiCorrect = null;
      try {
        const formData = new FormData();
        formData.append('file', wavBlob, 'test.wav');
        const predictBase = process.env.REACT_APP_PREDICT_URL || '/app';
        const res = await fetch(`${predictBase}/predict`, { method: 'POST', body: formData });
        if (res.status === 422) {
          const errData = await res.json();
          apiResult = errData;
          apiPass = false;
        } else if (res.ok) {
          const data = await res.json();
          apiResult = data;
          apiPass = !isLikelyNoiseResult(data);
        } else {
          apiResult = { error: `HTTP ${res.status}` };
          apiPass = null;
        }
      } catch (e) {
        apiResult = { error: e.message };
        apiPass = null;
      }

      if (apiPass !== null) {
        apiCorrect = apiPass === scenario.expectPass;
      }

      setPredictResult(apiResult);

      setResults(prev => [{
        id: Date.now(),
        scenario: scenario.label,
        expectPass: scenario.expectPass,
        clientPass,
        clientCorrect: correct,
        clientAnalysis: analysis,
        apiPass,
        apiCorrect,
        apiResult,
        timestamp: new Date().toLocaleTimeString(),
      }, ...prev].slice(0, 20));
    } finally {
      setIsAnalyzing(false);
    }
  }, [selectedScenario]);

  const correctCount = results.filter(r => r.clientCorrect && (r.apiCorrect !== false)).length;
  const totalCount = results.length;

  return (
    <div className="xi-public xi-audit">
      <div className="xi-public__veil" aria-hidden="true" />
      <div className="xi-public__body xi-audit__body">
        <header className="xi-page-head">
          <div className="xi-page-head__main">
            <span className="xi-eyebrow">Quality assurance</span>
            <h1 className="xi-page-head__title">Audio analysis test tool</h1>
            <p className="xi-page-head__sub">
              Test the voice detection system with different audio scenarios. Record audio and
              see whether the client-side analysis and predict API correctly detect speech
              versus non-speech.
            </p>
          </div>
        </header>

        <section className="xi-card">
          <div className="xi-card__head">
            <div>
              <h2 className="xi-card__title">Select a test scenario</h2>
              <p className="xi-card__sub">Each scenario has an expected pass or reject outcome.</p>
            </div>
            <span className="xi-badge xi-badge--brand">{TEST_SCENARIOS.length} scenarios</span>
          </div>
          <div className="xi-audit__scenarios" role="radiogroup" aria-label="Test scenario">
            {TEST_SCENARIOS.map(s => {
              const selected = selectedScenario === s.id;
              return (
                <button
                  key={s.id}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  className={`xi-audit__scenario${selected ? ' is-selected' : ''}`}
                  onClick={() => setSelectedScenario(s.id)}
                >
                  <span className="xi-audit__scenario-label">{s.label}</span>
                  <span className="xi-audit__scenario-desc">{s.desc}</span>
                  <span className={`xi-badge ${s.expectPass ? 'xi-badge--ok' : 'xi-badge--bad'}`}>
                    {s.expectPass ? 'Pass (speech)' : 'Reject (noise)'}
                  </span>
                </button>
              );
            })}
          </div>
        </section>

        <section className="xi-card">
          <div className="xi-row xi-row--between">
            <div className="xi-row">
              <button
                type="button"
                className={`xi-btn ${isRecording ? 'xi-btn--danger' : 'xi-btn--primary'}`}
                onClick={isRecording ? stopRecording : startRecording}
                disabled={isAnalyzing}
              >
                {isRecording ? (
                  <LuCircleStop aria-hidden="true" />
                ) : isAnalyzing ? (
                  <LuLoaderCircle className="xi-enroll__spin" aria-hidden="true" />
                ) : (
                  <LuMic aria-hidden="true" />
                )}
                {isRecording ? `Stop (${timer}s)` : isAnalyzing ? 'Analyzing…' : 'Record 8s'}
              </button>
              {liveMetrics && (
                <div className="xi-audit__metrics" aria-live="polite">
                  <span>Avg <strong>{liveMetrics.avgAmplitude}</strong></span>
                  <span>Peak <strong>{liveMetrics.peakAmplitude}</strong></span>
                  <span>Chunks <strong>{liveMetrics.chunkCount}</strong></span>
                </div>
              )}
            </div>
            {totalCount > 0 && (
              <span className="xi-badge xi-badge--info">
                Score {correctCount}/{totalCount} ({Math.round((correctCount / totalCount) * 100)}%)
              </span>
            )}
          </div>

          <canvas
            ref={waveformCanvas}
            width={860}
            height={80}
            className="xi-audit__wave"
            style={{ display: isRecording ? 'block' : 'none' }}
          />
        </section>

        {predictResult && (
          <section className="xi-card">
            <div className="xi-card__head">
              <h2 className="xi-card__title">Predict API response</h2>
            </div>
            <pre className="xi-audit__pre">{JSON.stringify(predictResult, null, 2)}</pre>
          </section>
        )}

        {results.length > 0 && (
          <section className="xi-card">
            <div className="xi-card__head">
              <div>
                <h2 className="xi-card__title">Test results</h2>
                <p className="xi-card__sub">Most recent first — up to 20 runs retained.</p>
              </div>
              <button type="button" className="xi-btn xi-btn--sm" onClick={() => setResults([])}>
                <LuTrash2 aria-hidden="true" />
                Clear results
              </button>
            </div>
            <div className="xi-table-wrap">
              <table className="xi-table">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Scenario</th>
                    <th>Expected</th>
                    <th>Client</th>
                    <th>Client OK</th>
                    <th>API</th>
                    <th>API OK</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map(r => (
                    <tr key={r.id}>
                      <td>{r.timestamp}</td>
                      <td>{r.scenario}</td>
                      <td>
                        {r.expectPass ? (
                          <span className="xi-badge xi-badge--ok">Pass</span>
                        ) : (
                          <span className="xi-badge xi-badge--bad">Reject</span>
                        )}
                      </td>
                      <td>{r.clientPass ? 'Pass' : 'Reject'}</td>
                      <td>
                        <ResultBadge passed={r.clientPass} expected={r.expectPass} />
                      </td>
                      <td>{r.apiPass === null ? 'Error' : r.apiPass ? 'Pass' : 'Reject'}</td>
                      <td>
                        {r.apiCorrect === null ? (
                          '—'
                        ) : (
                          <ResultBadge passed={r.apiPass} expected={r.expectPass} />
                        )}
                      </td>
                      <td className="xi-audit__details">{r.clientAnalysis.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
